import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, CircleAlert, KeyRound, Languages } from 'lucide-react'
import { useT, CATALOGS, LANG_LABELS, type Lang } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'

/** Katalogdagi barcha barg (string/funksiya) qiymatlarni rekursiv yig'adi. */
function collectLeaves(obj: unknown, out: string[] = []): string[] {
  if (typeof obj === 'string') {
    out.push(obj)
  } else if (obj && typeof obj === 'object') {
    for (const v of Object.values(obj as Record<string, unknown>)) collectLeaves(v, out)
  }
  // funksiya-qiymatlar (parametrli matnlar) tarjima holatida hisobga olinmaydi.
  return out
}

/**
 * A22.01 — Tillar ro'yxati + tarjima-qamrov. Manba: klient i18n kataloglari
 * (`CATALOGS`). Kalitlar TS-tip bilan majburlangan → strukturaviy qamrov 100%;
 * shu bois "tarjimasiz" = qiymati inglizcha (en) bilan AYNAN bir xil (evristika).
 */
export function Localization() {
  const t = useT()
  const rows = useMemo(() => {
    const en = collectLeaves(CATALOGS.en)
    return (Object.keys(CATALOGS) as Lang[]).map((l) => {
      const leaves = collectLeaves(CATALOGS[l])
      // en bilan bir xil pozitsiyadagi qiymatlar (tarjimasiz ehtimoli).
      const sameAsEn = l === 'en' ? 0 : leaves.filter((v, i) => v === en[i]).length
      const translated = leaves.length - sameAsEn
      const pct = leaves.length ? Math.round((translated / leaves.length) * 100) : 0
      return { lang: l, total: leaves.length, sameAsEn, pct }
    })
  }, [])

  return (
    <>
      <PageHeader
        title={t.loc.title}
        screenId="A22.01"
        description={t.loc.note}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/localization/keys"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <KeyRound size={13} />
              {t.loc.keysTitle}
            </Link>
            <Link
              to="/localization/missing"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <CircleAlert size={13} />
              {t.loc.missingTitle}
            </Link>
            <Link
              to="/localization/stats"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BarChart3 size={13} />
              {t.loc.statsTitle}
            </Link>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-2xl overflow-hidden rounded-md border border-border-default">
          <div className="flex items-center gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            <span className="w-40">{t.loc.language}</span>
            <span className="w-24 text-right">{t.loc.keys}</span>
            <span className="w-32 text-right">{t.loc.untranslated}</span>
            <span className="flex-1 text-right">{t.loc.coverage}</span>
          </div>
          {rows.map((r) => (
            <div key={r.lang} className="flex items-center gap-3 border-b border-border-subtle px-3 py-2 text-sm last:border-0">
              <span className="w-40 text-primary">{LANG_LABELS[r.lang]}</span>
              <span className="w-24 text-right font-mono tabular text-secondary">{r.total.toLocaleString()}</span>
              <span className="w-32 text-right font-mono tabular text-secondary">
                {r.lang === 'en' ? '—' : r.sameAsEn.toLocaleString()}
              </span>
              <span className="flex flex-1 items-center justify-end gap-2">
                <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-3">
                  <span className="block h-full rounded-full bg-accent" style={{ width: `${r.pct}%` }} />
                </span>
                <span className="w-10 text-right font-mono text-xs tabular text-tertiary">{r.pct}%</span>
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex max-w-2xl items-start gap-2 text-xs text-tertiary">
          <Languages size={14} className="mt-0.5 shrink-0" />
          <span>{t.loc.heuristic}</span>
        </div>
      </div>
    </>
  )
}
